import gql from 'graphql-tag'

export const CONTACT_DETAILS_FRAGMENT = gql`
  fragment ContactDetailsFragment on ContactDetails {
    phone
    email
    notes
    address
    vat
  }
`

export const CLIENT_RESPONSE_FRAGMENT = gql`
  fragment ClientResponseFragment on Client {
    id
    isActive
    name
    contactDetails {
      ...ContactDetailsFragment
    }
  }
  ${CONTACT_DETAILS_FRAGMENT}
`

export const CONTACT_FRAGMENT = gql`
  fragment ContactFragment on Contact {
    id
    firstName
    lastName
    jobTitle
    contactDetails {
      ...ContactDetailsFragment
    }
  }
  ${CONTACT_DETAILS_FRAGMENT}
`

export const CLIENT_RESPONSE_FULL_FRAGMENT = gql`
  fragment ClientResponseFullFragment on Client {
    ...ClientResponseFragment
    contacts {
      ...ContactFragment
    }
  }
  ${CLIENT_RESPONSE_FRAGMENT}
  ${CONTACT_FRAGMENT}
`

export const CLIENT_FRAGMENT = gql`
  fragment ClientFragment on Client {
    id
    name
  }
`

export const PRODUCER_FRAGMENT = gql`
  fragment ProducerFragment on Producer {
    id
    isActive
    name
    contactDetails {
      ...ContactDetailsFragment
    }
  }
  ${CONTACT_DETAILS_FRAGMENT}
`

export const PRODUCER_RESPONSE_FULL_FRAGMENT = gql`
  fragment ProducerResponseFullFragment on Producer {
    ...ProducerFragment
    contacts {
      ...ContactFragment
    }
  }
  ${PRODUCER_FRAGMENT}
  ${CONTACT_FRAGMENT}
`

export const PROJECT_TYPE_FRAGMENT = gql`
  fragment ProjectTypeFragment on ProjectType {
    id
    name
    isActive
  }
`

export const TIME_FACTOR_FRAGMENT = gql`
  fragment TimeFactorFragment on TimeFactor {
    id
    name
    isActive
  }
`

export const JOB_TITLE_FRAGMENT = gql`
  fragment JobTitleFragment on JobTitle {
    id
    name
    isActive
  }
`

export const SIZE_FRAGMENT = gql`
  fragment SizeFragment on Size {
    id
    isActive
    name
  }
`

export const SIZE_STANDARD_FRAGMENT = gql`
  fragment SizeStandardFragment on SizeStandard {
    id
    name
    isActive
    sizes {
      ...SizeFragment
    }
  }
  ${SIZE_FRAGMENT}
`

export const ROLE_FRAGMENT = gql`
  fragment RoleFragment on Role {
    id
    name
    application
  }
`

export const ACCOUNT_FRAGMENT = gql`
  fragment AccountFragment on Account {
    id
    isActive @client
    fullName @client
    firstName @client
    lastName @client
    email @client
    mobilePhone @client
    lang @client
    datePreferences @client
    timezone @client
    havelockEnabled @client
    prodactiveEnabled @client
    isGuest @client
    jobTitle @client {
      id
      name
    }
  }
`

export const ACCOUNT_FULL_FRAGMENT = gql`
  fragment AccountFullFragment on Account {
    id
    isActive
    fullName
    email
    mobilePhone
    firstName
    lastName
    lang
    datePreferences
    timezone
    havelockEnabled
    prodactiveEnabled
    isTeamLead
    isGuest
    teams {
      id
      name
    }
    profileImage {
      id
      presignedUrl @nonreactive
      thumbnailUrl @nonreactive
    }
    jobTitle {
      id
      name
    }
    roles {
      ...RoleFragment
    }
  }
  ${ROLE_FRAGMENT}
`

export const CURRENT_USER_ACCOUNT_FRAGMENT = gql`
  fragment CurrentUserAccountFragment on Account {
    id
    isActive
    fullName
    email
    mobilePhone
    firstName
    lastName
    lang
    datePreferences
    timezone
    havelockEnabled
    prodactiveEnabled
    isTeamLead
    profileImage {
      id
      presignedUrl @nonreactive
      thumbnailUrl @nonreactive
    }
    jobTitle {
      id
      name
    }
    permissions {
      name
      application
    }
    isGoogleCalendarEnabled
    isOutlookCalendarEnabled
    playThreadNotificationSound
    showOnlyNewThreadsActivity
    isGuest
  }
`

export const INVITATION_FRAGMENT = gql`
  fragment InvitationFragment on Invitation {
    status
    id
    inviteeEmail
    createdAt {
      seconds
    }
    expiresAt {
      seconds
    }
    inviter {
      ...AccountFragment
    }
    roles {
      ...RoleFragment
    }
  }
  ${ACCOUNT_FRAGMENT}
  ${ROLE_FRAGMENT}
`

export const BASE_MILESTONE_FRAGMENT = gql`
  fragment BaseMilestoneFragment on BaseMilestone {
    id
    name
    isActive
    isCheckpoint
  }
`

export const TEAM_MEMBER_FRAGMENT = gql`
  fragment TeamMemberFragment on TeamMember {
    account {
      ...AccountFragment
    }
    isGuest
  }
  ${ACCOUNT_FRAGMENT}
`

export const TEAM_FRAGMENT = gql`
  fragment TeamFragment on Team {
    id
    isActive @client
    name @client
    teamLead @client {
      ...AccountFragment
    }
    members @client {
      ...TeamMemberFragment
    }
  }
  ${ACCOUNT_FRAGMENT}
  ${TEAM_MEMBER_FRAGMENT}
`

export const TEAM_MEMBER_ACTIVITY_FRAGMENT = gql`
  fragment TeamMemberActivityFragment on TeamMemberActivity {
    teamMember {
      ...TeamMemberFragment
    }
    tasks {
      activeTasks
      completedTasks
    }
    goals {
      activeGoals
      completedGoals
    }
  }
  ${ACCOUNT_FRAGMENT}
`

export const TEAM_FULL_FRAGMENT = gql`
  fragment TeamFullFragment on Team {
    id
    isActive
    isGuest
    name
    teamLead {
      ...AccountFragment
    }
    members {
      ...TeamMemberFragment
    }
  }
  ${ACCOUNT_FRAGMENT}
  ${TEAM_MEMBER_FRAGMENT}
`

export const LABEL_FRAGMENT = gql`
  fragment LabelFragment on Label {
    isActive
    id
    name
    color
  }
`
export const PROCEDURE_TASK_FRAGMENT = gql`
  fragment ProcedureTaskFragment on ProcedureTask {
    id
    name
    description
    assignees {
      account {
        ...AccountFragment
      }
      estimatedHours
    }
    estimatedHours
  }
  ${ACCOUNT_FRAGMENT}
`

export const PROCEDURE_MILESTONE_FRAGMENT = gql`
  fragment ProcedureMilestoneFragment on ProcedureMilestone {
    id
    name
    position
    procedureId
    baseMilestoneId
    notes
    duration
    isCheckpoint
    tasks {
      ...ProcedureTaskFragment
    }
    milestoneDependencyIds
    label {
      ...LabelFragment
    }
  }
  ${PROCEDURE_TASK_FRAGMENT}
  ${LABEL_FRAGMENT}
`

export const PROCEDURE_MILESTONE_FULL_FRAGMENT = gql`
  fragment ProcedureMilestoneFullFragment on ProcedureMilestone {
    id
    name
    position
    notes
    procedureId
    baseMilestoneId
    milestoneDependencyIds
    label {
      ...LabelFragment
    }
  }
  ${LABEL_FRAGMENT}
`

export const PROCEDURE_MILESTONE_LABEL_AND_DEPENDENCIES_FRAGMENT = gql`
  fragment ProcedureMilestoneLabelAndDependenciesFragment on ProcedureMilestone {
    id
    milestoneDependencyIds
    notes
    label {
      ...LabelFragment
    }
  }
  ${LABEL_FRAGMENT}
`

export const PROCEDURE_WITH_PARTIAL_MILESTONES_FRAGMENT = gql`
  fragment ProcedureWithPartialMilestonesFragment on Procedure {
    id
    milestones {
      ...ProcedureMilestoneLabelAndDependenciesFragment
    }
  }
  ${PROCEDURE_MILESTONE_LABEL_AND_DEPENDENCIES_FRAGMENT}
`

export const CATEGORY_FRAGMENT = gql`
  fragment CategoryFragment on Category {
    id
    name
    isActive
  }
`

export const IMAGE_PREVIEW_FRAGMENT = gql`
  fragment ImagePreviewFragment on Resource {
    id
    presignedUrl
    contentType
  }
`

export const RESOURCE_FRAGMENT = gql`
  fragment ResourceFragment on Resource {
    id
    project {
      id
      access
    }
    milestone {
      id
    }
    task {
      id
    }
    goal {
      id
    }
    isInline
    key
    commentId
    contentType
    isLink
    presignedUrl
    access
    userAccess
    name
    alias
    size
    createdAt {
      seconds
    }
    account {
      ...AccountFragment
    }
    category {
      ...CategoryFragment
    }
  }
  ${ACCOUNT_FRAGMENT}
  ${CATEGORY_FRAGMENT}
`

export const RESOURCE_FRAGMENT_WITH_ENTITY_NAMES = gql`
  fragment ResourceFragmentWithEntityNames on Resource {
    ...ResourceFragment
    project {
      id
      name
      access
    }
    milestone {
      id
      name
    }
    task {
      id
      name
    }
    goal {
      id
      name
    }
  }
  ${RESOURCE_FRAGMENT}
`

export const USER_CONTRIBUTOR_FRAGMENT = gql`
  fragment UserContributorFragment on UserContributor {
    access
    account {
      ...AccountFragment
    }
  }
  ${ACCOUNT_FRAGMENT}
`

export const CUSTOM_FIELD_DEFINITION_FRAGMENT = gql`
  fragment CustomFieldDefinitionFragment on CustomFieldDefinition {
    id
    table
    name
    type
    enumEntries {
      displayName
      value
      position
    }
    description
    tooltip
    isActive
    createdBy {
      id
    }
    userContributors {
      ...UserContributorFragment
    }
    teamContributors {
      team {
        ...TeamFragment
      }
      contributors {
        ...UserContributorFragment
      }
    }
  }
  ${TEAM_FRAGMENT}
  ${USER_CONTRIBUTOR_FRAGMENT}
`

export const CUSTOM_FIELD_VALUE_FRAGMENT = gql`
  fragment CustomFieldValueFragment on CustomFieldValue {
    definition {
      ...CustomFieldDefinitionFragment
    }
    stringValue
    numberValue
    dateValue {
      seconds
    }
    idValue
    multiStringValue
    multiIdValue
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`

export const PROCEDURE_FRAGMENT = gql`
  fragment ProcedureFragment on Procedure {
    id
    title
    isActive
    notes
    milestones {
      ...ProcedureMilestoneFragment
    }
    resources {
      ...ResourceFragment
    }
    projectType {
      ...ProjectTypeFragment
    }
    clients {
      ...ClientFragment
    }
    reference
    projectNotes
    quantity
    hasPriority
    timeFactor {
      ...TimeFactorFragment
    }
    image {
      ...ResourceFragment
    }
    producers {
      ...ProducerFragment
    }
    customFieldValues {
      ...CustomFieldValueFragment
    }
    createdAt {
      seconds
    }
  }
  ${PROCEDURE_MILESTONE_FRAGMENT}
  ${RESOURCE_FRAGMENT}
  ${ACCOUNT_FRAGMENT}
  ${CLIENT_FRAGMENT}
  ${PRODUCER_FRAGMENT}
  ${CUSTOM_FIELD_VALUE_FRAGMENT}
`

export const PROCEDURE_LIST_FRAGMENT = gql`
  fragment ProcedureListFragment on Procedure {
    id
    title
    isActive
    notes
    milestones {
      ...ProcedureMilestoneFragment
    }
    projectType {
      ...ProjectTypeFragment
    }
  }
  ${PROCEDURE_MILESTONE_FRAGMENT}
  ${PROJECT_TYPE_FRAGMENT}
`

export const RESOURCE_WITH_ENTITY_NAMES_FRAGMENT = gql`
  fragment ResourceWithEntityNamesFragment on Resource {
    id
    project {
      id
      name
    }
    milestone {
      id
      name
    }
    task {
      id
      name
    }
    commentId
    contentType
    isLink
    presignedUrl
    access
    userAccess
    name
    alias
    size
    createdAt {
      seconds
    }
    account {
      ...AccountFragment
    }
    category {
      ...CategoryFragment
    }
  }
  ${ACCOUNT_FRAGMENT}
  ${CATEGORY_FRAGMENT}
`

export const RESOURCE_ACCESS_FRAGMENT = gql`
  fragment ResourceAccessFragment on Resource {
    id
    access
    teamContributors {
      team {
        ...TeamFragment
      }
      contributors {
        ...UserContributorFragment
      }
    }
    userContributors {
      ...UserContributorFragment
    }
  }
  ${USER_CONTRIBUTOR_FRAGMENT}
  ${TEAM_FRAGMENT}
`

export const DELETE_RESOURCE_FRAGMENT = gql`
  fragment DeleteResourceFragment on DeleteResource {
    failedIds
  }
`
export const CALENDAR_FRAGMENT = gql`
  fragment CalendarFragment on Calendar {
    id
    name
    type
    isSelected
  }
`

export const CALENDAR_EVENT_FRAGMENT = gql`
  fragment CalendarEventFragment on CalendarEvent {
    fromDate {
      seconds
    }
    toDate {
      seconds
    }
    fromTime {
      seconds
    }
    toTime {
      seconds
    }
    link
    meetingLink
    name
  }
`

export const ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationFragment on Organization {
    id
    name
    currency
    language
    providerId
    enabledFeatures
    havelockEnabled
    prodactiveEnabled
    createdAt {
      seconds
    }
    logo {
      id
      presignedUrl
    }
  }
`

export const ORGANIZATION_FULL_FRAGMENT = gql`
  fragment OrganizationFullFragment on Organization {
    id
    providerId
    name
    enabledFeatures
    havelockEnabled
    prodactiveEnabled
    workDays
    workDayHours
    workCalendarExceptionPeriods {
      ...WorkCalendarExceptionPeriodFragment
    }
    createdAt {
      seconds
    }
    currency
    isActive
    language
    industry
    logo {
      ...ResourceFragment
    }
    notes
    type
    updatedAt {
      seconds
    }
  }
  ${RESOURCE_FRAGMENT}
`

export const WORK_CALENDAR_EXCEPTION_PERIOD_FRAGMENT = gql`
  fragment WorkCalendarExceptionPeriodFragment on WorkCalendarExceptionPeriod {
    id
    type
    notes
    startDate {
      seconds
    }
    endDate {
      seconds
    }
  }
`
